/** RFC 0345 3f: who answers a member's message. The session's agent does,
 * from the owner's Mac, unless the owner set Only I can (session-agents
 * memberAsks). This window tells the member which, and lets them bring
 * their own agent: a person whose own agent is on the session has their own
 * messages answered by it, signed on their device, and by no one else's.
 */
import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import { decodeLogDoc } from '@mod/core/log'
import type { SessionDoc, WorldDoc } from '@mod/core/model'
import type { createSharingRepo } from '@mod/core/sharing'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTabRepoBoot } from '../world/repo/TabRepoProvider'
import { useDocument } from '../world/repo/useDocument'
import { useSharingAccount } from './accountContext'

type Sharing = Awaited<ReturnType<typeof createSharingRepo>>

const KEY_ID = /^[0-9a-f]{64}$/

export interface ModelChoice {
  provider: string
  model: string
}

/** A message from a person, with the runs on the log that name it. */
export interface GuestMessage {
  entryId: string
  /** The person (group hex) the message claims. Verified before a card shows. */
  by: string
  /** Runs naming this message. Unverified: a planted run must not count as
   *  an answer, so verifiedAsks checks each before it hides a card. */
  runs?: string[]
}

export interface GuestReplyState {
  /** This person's newest live agent key on this session, as the editable
   *  `agentKeys` map labels it. Unverified: the hook checks who granted it. */
  agent?: string
  /** All of this person's labelled live keys, newest first. */
  agents: string[]
  /** This person's own recent messages, oldest first, with their runs.
   *  Unverified: an editor can write a line in anyone's name. */
  mine: GuestMessage[]
  /** The person whose agent the guest can ask: the newest other live key's. */
  askable?: string
}

/** How far back a person's unanswered messages are looked for. */
const RECENT = 20

/** What the log says, before any signature is checked. `me` is this
 * window's person. */
export function guestReplyState(input: {
  session: SessionDoc | undefined
  me: string | undefined
}): GuestReplyState {
  const { session, me } = input
  if (!session || !me || !KEY_ID.test(me)) return { agents: [], mine: [] }
  // A granted turn's key (RFC 0345 grid 8) is never an agent to pick or
  // to ask: it only runs the owner's turns answering one collaborator.
  const keys = Object.entries(session.agentKeys ?? {}).filter(
    ([, record]) =>
      record &&
      record.revokedAt == null &&
      (record as { grantFor?: unknown }).grantFor == null,
  )
  const agents = keys
    .filter(([, record]) => String(record.person) === me)
    .sort(([, a], [, b]) => Number(b.createdAt) - Number(a.createdAt))
    .map(([key]) => key)
  const mine = agents[0]
  const entries = Object.values(
    decodeLogDoc(session as unknown as Record<string, unknown>).entries,
  ).sort((a, b) => a.createdAt - b.createdAt)
  const runs = new Map<string, string[]>()
  for (const entry of entries) {
    if (entry.payload.type !== 'run') continue
    const prompt = String(entry.payload.payload.promptMessageId)
    runs.set(prompt, [...(runs.get(prompt) ?? []), entry.id])
  }
  let own: GuestMessage[] = []
  for (const entry of entries) {
    if (entry.payload.type !== 'message') continue
    const message = entry.payload.payload
    if (message.role !== 'user') continue
    const by = String(message.by ?? '')
    if (!KEY_ID.test(by)) continue
    const line = { entryId: entry.id, by, runs: runs.get(entry.id) ?? [] }
    if (by === me) own = [...own, line].slice(-RECENT)
  }
  const askable = keys
    .filter(([, record]) => String(record.person) !== me)
    .sort(
      ([, a], [, b]) => Number(b.createdAt) - Number(a.createdAt),
    )[0]?.[1]?.person
  return {
    ...(mine ? { agent: mine } : {}),
    agents,
    mine: own,
    ...(askable != null ? { askable: String(askable) } : {}),
  }
}

/** Whether any run naming this message is a reply its agent's person
 * really signed. */
export async function answered(
  sharing: Sharing,
  sessionId: string,
  message: GuestMessage,
): Promise<boolean> {
  if (!message.runs?.length) return false
  const [{ verifiedReplyRun }, { BranchLog }] = await Promise.all([
    import('@mod/core/sharing'),
    import('@mod/core/log'),
  ])
  const handle = await sharing.repo.find<SessionDoc>(sessionId as AnyDocumentId)
  const log = new BranchLog(handle as never)
  for (const runId of message.runs)
    if (await verifiedReplyRun(sharing, log, runId)) return true
  return false
}

/** Each person's messages since their last really answered one, keeping only
 * posts a device of that person signed while they can still write the
 * session. The log's `by` is editable text, and so is any run. */
export async function verifiedAsks(
  sharing: Sharing,
  sessionId: string,
  asks: GuestMessage[],
): Promise<GuestMessage[]> {
  if (!asks.length) return []
  const [{ verifiedSessionPost }, { BranchLog }] = await Promise.all([
    import('@mod/core/sharing'),
    import('@mod/core/log'),
  ])
  const handle = await sharing.repo.find<SessionDoc>(sessionId as AnyDocumentId)
  const log = new BranchLog(handle as never)
  const byPerson = new Map<string, GuestMessage[]>()
  for (const ask of asks)
    byPerson.set(ask.by, [...(byPerson.get(ask.by) ?? []), ask])
  const out: GuestMessage[] = []
  for (const lines of byPerson.values()) {
    const open: GuestMessage[] = []
    for (const ask of [...lines].reverse()) {
      if (await answered(sharing, sessionId, ask)) break
      const post = await verifiedSessionPost(sharing, log, ask.entryId)
      if (post?.by === ask.by) open.unshift(ask)
    }
    out.push(...open)
  }
  return out
}

/** A person's own message, answered by their own agent. Signed on their
 * device for their key; another person's agent is never named. */
export async function answerOwn(
  sharing: Sharing,
  input: {
    sessionId: string
    entryId: string
    me: string
    agent: string
    model?: ModelChoice
  },
): Promise<string> {
  if (!input.model)
    throw new Error('Choose a model in this session before your agent answers')
  const { queueSessionReply } = await import('@mod/core/sharing')
  return queueSessionReply(sharing, input.sessionId, input.entryId, {
    agent: input.agent,
    by: input.me,
    requestedModel: input.model,
  })
}

export type GuestChoice = 'add'

/** The member's line: whose agent answers them here, and whether they asked
 * for their own. */
export interface GuestCard {
  entryId: string
  owner: string
  /** Asked for their own agent, which joins once their Mac hosts it. */
  adding: boolean
  /** RFC 0345 A3: anyone here can ask the session's agent (the owner has
   *  not said only they can). */
  anyone: boolean
}

/** "Add my agent": a wish in this person's private World, which their own
 * daemon reads. No key is written from the window. */
export async function addMyAgent(
  sharing: Sharing,
  input: { sessionId: string; me: string; worldId: string },
): Promise<void> {
  const { addOwnAgent } = await import('@mod/core/sharing')
  await addOwnAgent(sharing, {
    person: input.me,
    sessionId: input.sessionId,
    worldId: input.worldId,
  })
}

/** The member's line, and a person's own agent answering them, for one
 * session. Draws nothing when sharing is off. `addAgent` replaces how this
 * window asks for the person's own agent (by default, the wish in their
 * World); with neither, Add my agent is not offered. */
export function useGuestReplies(
  sessionId: string,
  options: {
    /** This person's own model choice on this device (the composer's).
     *  Never read off the log, where a guest could pick what Nassar spends. */
    model?: ModelChoice
    addAgent?: (sessionId: string) => Promise<void>
  } = {},
): {
  waiting?: string
  guest?: GuestCard
  chooseGuest: (choice: GuestChoice) => Promise<void>
  canAddAgent: boolean
  error: string
} {
  const { sharing } = useTabRepoBoot()
  const account = useSharingAccount()
  const me = account?.opened.location.personId
  const worldId = account?.opened.location.worldId
  const [session] = useDocument<SessionDoc>(sessionId as AnyDocumentId)
  const [world] = useDocument<WorldDoc>(
    (worldId ?? undefined) as AnyDocumentId | undefined,
  )
  const adding = world?.[`ownAgent:${sessionId}`] != null
  const add =
    options.addAgent ??
    (sharing && me && worldId
      ? (id: string) => addMyAgent(sharing, { sessionId: id, me, worldId })
      : undefined)
  const [error, setError] = useState('')
  const tried = useRef(new Set<string>())
  const model =
    options.model?.provider.trim() && options.model.model.trim()
      ? options.model
      : undefined
  const state: GuestReplyState = sharing
    ? guestReplyState({ session, me })
    : { agents: [], mine: [] }
  // Runs are part of the key: a run arriving can answer a message.
  const lineKey = (line: GuestMessage) =>
    `${line.entryId}:${(line.runs ?? []).join('|')}`
  const mineKey = state.mine.map(lineKey).join(',')
  const modelKey = model ? JSON.stringify(model) : ''
  const agentsKey = state.agents.join(',')
  // The agent this person really has here: the newest labelled key a device
  // of theirs granted. A key planted in `agentKeys` with their name is not it.
  const [agent, setAgent] = useState<string | undefined>()
  // biome-ignore lint/correctness/useExhaustiveDependencies: agentsKey stands for the candidates
  useEffect(() => {
    if (!sharing || !me || !state.agents.length) {
      setAgent(undefined)
      return
    }
    let live = true
    void (async () => {
      const { sessionAgentOf } = await import('@mod/core/sharing')
      for (const key of state.agents)
        if (await sessionAgentOf(sharing, sessionId, key, me)) {
          if (live) setAgent(key)
          return
        }
      if (live) setAgent(undefined)
    })()
    return () => {
      live = false
    }
  }, [sharing, sessionId, me, agentsKey])

  // This person's newest message they really posted, while no run naming
  // it verifies. A line planted in their name is not theirs to answer.
  const [lastOpen, setLastOpen] = useState<string | undefined>()
  // biome-ignore lint/correctness/useExhaustiveDependencies: mineKey stands for the messages and their runs
  useEffect(() => {
    if (!sharing || !state.mine.length) {
      setLastOpen(undefined)
      return
    }
    let live = true
    void verifiedAsks(sharing, sessionId, state.mine)
      .catch(() => [] as GuestMessage[])
      .then((open) => {
        if (live) setLastOpen(open.at(-1)?.entryId)
      })
    return () => {
      live = false
    }
  }, [sharing, sessionId, mineKey])
  // Someone else's agent must be here for "waiting" to mean anything; your
  // own agent answering you is not a guest's wait.
  const waiting = lastOpen && !agent && state.askable ? lastOpen : undefined

  // A person's own agent answers their own newest message, once per window.
  // queueSessionReply reuses a run any of their devices already signed.
  const own = agent ? lastOpen : undefined
  // biome-ignore lint/correctness/useExhaustiveDependencies: modelKey stands for the model
  useEffect(() => {
    if (!sharing || !me || !own || !agent || !model) return
    if (tried.current.has(own)) return
    tried.current.add(own)
    answerOwn(sharing, {
      sessionId,
      entryId: own,
      me,
      agent,
      model,
    }).catch((e) =>
      setError(e instanceof Error ? e.message : 'Your agent could not answer'),
    )
  }, [sharing, sessionId, me, own, agent, modelKey])

  const chooseGuest = useCallback(
    async (choice: GuestChoice) => {
      if (choice !== 'add' || !waiting || !add) return
      try {
        await add(sessionId)
        setError('')
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not add your agent')
      }
    },
    [sessionId, waiting, add],
  )

  return {
    ...(waiting ? { waiting } : {}),
    ...(waiting && state.askable
      ? {
          guest: {
            entryId: waiting,
            owner: state.askable,
            adding,
            anyone:
              (session as { askAgent?: unknown } | undefined)?.askAgent !==
              'owner',
          },
        }
      : {}),
    chooseGuest,
    canAddAgent: add != null && !adding,
    error,
  }
}
